import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import i18n, { use } from 'i18next';
import { useTranslation } from "react-i18next";
import { Menu, Pagination, Select } from 'antd';

import FormHeader from "../components/form/FormHeader";
import FormTitle from "../components/form/FormTitle";
import FormInput from "../components/form/FormInput";
import FormSelect from "../components/form/FormSelect";
import FormSelect2 from "../components/form/FormSelect2";
import FormRemark from "../components/form/FormRemark";
import FormText from "../components/form/FormText";
import FormField from "../components/form/FormField";
import FormLabel from "../components/form/FormLabel";
import FormInputError from "../components/form/FormInputError";
import FormCheckbox from "../components/form/FormCheckbox";
import Flex from "../components/core/Flex";
import ShadowBox from "../components/core/ShadowBox";
import DropdownControl from "../components/core/DropdownControl";
import HeaderBar from "../components/admin/HeaderBar";
import useModalStore from "../store/useModalStore";
import useAdminAccountStore from "../store/useAdminAccountStore";
import useAuthAdminStore from "../store/useAuthAdminStore";
import useViewStore from "../store/useViewStore";
import withErrorHandling from "../function/withErrorHandling";
import Space from "../components/core/Space";
import { apiDomain, basename } from "../setting";

function AdminAccount() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openModalInfo, openModalConfirm, closeModalConfirm } = useModalStore();
  const { isMobileView } = useViewStore();

  const { firstName, lastName, role } = useAuthAdminStore();
  const { isSortingOpen, setIsSortingOpen, sort, setSort, role_filter } = useAdminAccountStore();
  const { selectedTab, setSelectedTab } = useAdminAccountStore();
  const { searchText, setSearchText } = useAdminAccountStore();
  const { page, recordperpage, ttlpage, setPage } = useAdminAccountStore();
  const { CSCount, ClmCount, AllCount } = useAdminAccountStore();
  const { admListStaff, admOpenStaff, admUpdateStaff, admUpdateStaffStatus, resetAccountModal } = useAdminAccountStore();
  const { accountList } = useAdminAccountStore();

  const allowEdit = (role, item_role) => {
    if (role === "SUPERADM") {
      return true;
    }

    if (role === "CSVIEW" || role === "CLMHDR") {
      return false;
    }

    if (role === "CSADM" && (item_role === "CSVIEW" || item_role === "CSADM")) {
      return true;
    } else if (role === "CLMADM" && (item_role === "CLMHDR" || item_role === "CLMADM")) {
      return true;
    }

    return false;
  }

  const items = [
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'CLM'}>{t("translation:admin.account.tab.claim_headler_team")}</MenuItemText><MenuItemCount $active={selectedTab === 'CLM'}>({ClmCount})</MenuItemCount></MenuItem>,
      key: 'CLM',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'CS'}>{t("translation:admin.account.tab.cs_team")}</MenuItemText><MenuItemCount $active={selectedTab === 'CS'}>({CSCount})</MenuItemCount></MenuItem>,
      key: 'CS',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'ALL'}>{t("translation:admin.account.tab.all_account")}</MenuItemText><MenuItemCount $active={selectedTab === 'ALL'}>({AllCount})</MenuItemCount></MenuItem>,
      key: 'ALL',
    },
  ];

  const ContentAccountForm = () => {
    const { roleList } = useAdminAccountStore();
    const { role } = useAuthAdminStore();
    const { firstName, lastName, email, lanId, role: role_modal } = useAdminAccountStore();
    const { setRole, setFirstName, setLastName, setEmail, setLanId } = useAdminAccountStore();
    const { isValid_role, isValid_firstName, isValid_lastName, isValid_email, isValid_lanId } = useAdminAccountStore();
    const { setIsValid_role, setIsValid_firstName, setIsValid_lastName, setIsValid_email, setIsValid_lanId } = useAdminAccountStore();

    // const { notify, setNotify } = useAdminAccountStore();

    return (
      <Flex gap={16} direction={"column"} alignItems={"flex-start"}>
        <Space height={0} />
        <FormField style={{ width: 300, marginBottom: 0 }}>
          <FormLabel>{t("translation:admin.account.list_field.role")}</FormLabel>
          <FormSelect2
            placeholder={t("translation:admin.account.list_field.role")}
            size={"large"}
            allowClear
            options={[
              ...roleList.filter((item) => {
                return allowEdit(role, item.role);
              }).map((item) => {
                return {
                  value: item.role,
                  label: t(`translation:admin.common.role.${item.role}` ?? "")
                };
              })
            ]}
            value={role_modal}
            onChange={(value) => {
              setRole(value);
              setIsValid_role(true);
            }}
            style={{
              width: "100%",
            }}
          />
          <FormInputError show={!isValid_role}>* {t("pleaseSelect", { key: t("translation:admin.account.list_field.role") })}</FormInputError>
        </FormField>

        <FormField style={{ width: "100%", marginBottom: 0 }}>
          <FormLabel htmlFor="first_name">{t("translation:admin.account.list_field.first_name")}</FormLabel>
          <FormInput id="first_name" value={firstName} handleChange={e => { setFirstName(e.target.value); setIsValid_firstName(true); }} placeholder={t("pleaseEnter", { key: t("translation:admin.account.list_field.first_name") })} aria-label={t("translation:admin.account.list_field.first_name")} />
          <FormInputError show={!isValid_firstName}>* {t("pleaseEnter", { key: t("translation:admin.account.list_field.first_name") })}</FormInputError>
        </FormField>

        <FormField style={{ width: "100%", marginBottom: 0 }}>
          <FormLabel htmlFor="last_name">{t("translation:admin.account.list_field.last_name")}</FormLabel>
          <FormInput id="last_name" value={lastName} handleChange={e => { setLastName(e.target.value); setIsValid_lastName(true); }} placeholder={t("pleaseEnter", { key: t("translation:admin.account.list_field.last_name") })} aria-label={t("translation:admin.account.list_field.last_name")} />
          <FormInputError show={!isValid_lastName}>* {t("pleaseEnter", { key: t("translation:admin.account.list_field.last_name") })}</FormInputError>
        </FormField>

        <FormField style={{ width: "100%", marginBottom: 0 }}>
          <FormLabel htmlFor="email">{t("translation:admin.account.list_field.email")}</FormLabel>
          <FormInput id="email" value={email} handleChange={e => { setEmail(e.target.value); setIsValid_email(true); }} placeholder={t("pleaseEnter", { key: t("translation:admin.account.list_field.email") })} aria-label={t("translation:admin.account.list_field.email")} />
          <FormInputError show={!isValid_email}>* {t("pleaseEnter", { key: t("translation:admin.account.list_field.email") })}</FormInputError>
        </FormField>

        <FormField style={{ width: "100%", marginBottom: 0 }}>
          <FormLabel htmlFor="lanId">{t("translation:admin.account.list_field.lanID")}</FormLabel>
          <FormInput id="lanId" value={lanId} handleChange={e => { setLanId(e.target.value); setIsValid_lanId(true); }} placeholder={t("pleaseEnter", { key: t("translation:admin.account.list_field.lanID") })} aria-label={t("translation:admin.account.list_field.lanID")} />
          <FormInputError show={!isValid_lanId}>* {t("pleaseEnter", { key: t("translation:admin.account.list_field.lanID") })}</FormInputError>
        </FormField>

        {/* <FormCheckbox active={notify} onClick={() => { setNotify(!notify); }} style={{ marginTop: 10, paddingLeft: 0 }}>
          Notify this user by email
        </FormCheckbox> */}
      </Flex >
    );
  };

  const openFilter = () => {
    openModalConfirm({
      title: t("translation:admin.common.filter"),
      modalConfirmPrimaryButtonText: t("translation:admin.common.filter"),
      modalConfirmSecondaryButtonText: t("back"),
      content: <ContentFilter />,
      style: { content: { width: 720 } },
      callback: () => {
        loadAdmListStaff();
      },
    });
  }

  const ContentFilter = () => {
    const { role_filter, roleList, setRoleFilter } = useAdminAccountStore();

    return <Flex direction={"column"} alignItems={"flex-start"}>
      <Flex style={{ width: "100%" }} alignItems={"flex-start"} direction={isMobileView ? "column" : "row"} gap={isMobileView ? 5 : 20}>
        <FormField style={{ marginBottom: 8 }}>
          <FormLabel>{t("translation:admin.account.list_field.role")}</FormLabel>
          <FormSelect2
            placeholder={t("translation:admin.account.list_field.role")}
            size={"large"}
            allowClear
            options={[
              ...roleList.map((item) => {
                return {
                  label: item.roleDesc,
                  value: item.role,
                }
              })
            ]}
            value={role_filter}
            onChange={(value) => {
              setRoleFilter(value)
            }}
            style={{
              width: "100%",
            }}
          />
        </FormField>
      </Flex>
    </Flex>
  }

  const loadAdmListStaff = withErrorHandling(admListStaff);
  const loadAdmOpenStaff = withErrorHandling(admOpenStaff);
  const loadAdmUpdateStaff = withErrorHandling(admUpdateStaff);
  const loadAdmUpdateStaffStatus = withErrorHandling(admUpdateStaffStatus);

  useEffect(() => {
    loadAdmListStaff();
  }, [selectedTab, sort, role_filter, page]);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadAdmListStaff();
    }, 300);

    return () => clearTimeout(delay);
  }, [searchText]);

  return (
    <PageApplication>
      <HeaderBar title={t("translation:admin.portal_name")} name={`${firstName} ${lastName}`} role={role} />

      <PageContainer>
        <Flex>
          <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.account.title")}</FormHeader>
        </Flex>

        <MenuContainer>
          <Menu onClick={(e) => {
            setSelectedTab(e.key);
          }} selectedKeys={[selectedTab]} mode="horizontal" items={items} style={{ minWidth: 0, flex: "auto" }} />

          <MenuExtend>
            <ExportBtn onClick={() => {
              resetAccountModal();
              openModalConfirm({
                title: t("translation:admin.account.add_account.title"),
                text: t("translation:admin.account.add_account.subtitle"),
                modalConfirmPrimaryButtonText: t("create"),
                modalConfirmSecondaryButtonText: t("back"),
                closeManually: true,
                content: <ContentAccountForm />,
                style: { content: { width: 720 } },
                callback: async () => {
                  const { role, firstName, lastName, email, lanId } = useAdminAccountStore.getState();
                  const { setIsValid_role, setIsValid_firstName, setIsValid_lastName, setIsValid_email, setIsValid_lanId } = useAdminAccountStore.getState();

                  setIsValid_role(true);
                  setIsValid_firstName(true);
                  setIsValid_lastName(true);
                  setIsValid_email(true);
                  setIsValid_lanId(true);

                  let isValid = true;

                  if (!role) {
                    isValid = false;
                    setIsValid_role(false);
                  }

                  if (firstName === "") {
                    isValid = false;
                    setIsValid_firstName(false);
                  }

                  if (lastName === "") {
                    isValid = false;
                    setIsValid_lastName(false);
                  }

                  if (email === "") {
                    isValid = false;
                    setIsValid_email(false);
                  } else if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(email)) {
                    isValid = false;
                    setIsValid_email(false);
                  }

                  if (lanId === "") {
                    isValid = false;
                    setIsValid_lanId(false);
                  }

                  if (isValid) {
                    await loadAdmUpdateStaff();
                    closeModalConfirm();

                    openModalInfo({
                      title: "New Account",
                      isShowSuccessIcon: true,
                      text: `Account has been created`,
                    });
                  }
                },
              });
            }}>
              <ExportBtnIcon src={`${process.env.PUBLIC_URL}/images/plus.png`} alt={t("translation:admin.account.add_account.title")} />
              <ExportBtnText>{t("translation:admin.account.add_account.title")}</ExportBtnText>
            </ExportBtn>
          </MenuExtend>
        </MenuContainer>

        <ToolBar>
          <ToolBarLeft>
            <FormInput
              prefix={<SearchIcon src={`${process.env.PUBLIC_URL}/images/magnifier.png`} alt={t("translation:admin.common.search")} />}
              placeholder={t("translation:admin.common.search")}
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); }}
              style={{
                width: 300,
              }}
            />
          </ToolBarLeft>
          <ToolBarRight>
            <ToolBarItem onClick={() => { openFilter(); }}>
              <ToolBarItemIcon src={`${process.env.PUBLIC_URL}/images/slidebars.png`} alt={t("translation:admin.common.filter")} />
              <ToolBarItemText>{t("translation:admin.common.filter")}</ToolBarItemText>
            </ToolBarItem>
            <DropdownControl
              handleBlur={() => { setIsSortingOpen(false); }}
              placement={"right"}
              clickAndClose={true}
              open={isSortingOpen}
              // open={true}
              dropdown={
                <>
                  <FilterItem onClick={() => { setSort("createdt desc"); }}>
                    <FormTitle>{t("translation:admin.account.list_field.createdt")} {t("translation:admin.common.sort.desc")}</FormTitle>
                  </FilterItem>
                  <FilterItem onClick={() => { setSort("createdt asc"); }}>
                    <FormTitle>{t("translation:admin.account.list_field.createdt")} {t("translation:admin.common.sort.asc")}</FormTitle>
                  </FilterItem>
                </>
              }
            >
              <ToolBarItem onClick={() => { setIsSortingOpen(!isSortingOpen); }}>
                <ToolBarItemIcon src={`${process.env.PUBLIC_URL}/images/chevron-ud.png`} alt={t("translation:admin.common.sort.title")} />
                <ToolBarItemText>{t("translation:admin.common.sort.title")}</ToolBarItemText>
              </ToolBarItem>
            </DropdownControl>
          </ToolBarRight>
        </ToolBar>

        <ItemList>
          {
            accountList.map((item, index) => {
              return (
                <Item key={item.staffId}>
                  <ItemLeft>
                    <Info style={{ width: 90 }}>
                      <FormRemark>{t("translation:admin.account.list_field.team")}</FormRemark>
                      <FormText>{item.Team}</FormText>
                    </Info>
                    <Info style={{ width: 150 }}>
                      <FormRemark>{t("translation:admin.account.list_field.role")}</FormRemark>
                      <FormText style={{ fontWeight: "bold", color: "var(--aia-bx-dark-blue)" }}>{item.roleDesc}</FormText>
                    </Info>
                    <Info style={{ width: 80 }}>
                      <FormRemark>{t("translation:admin.account.list_field.first_name")}</FormRemark>
                      <FormText>{item.staffFirstName}</FormText>
                    </Info>
                    <Info style={{ width: 80 }}>
                      <FormRemark>{t("translation:admin.account.list_field.last_name")}</FormRemark>
                      <FormText>{item.staffLastName}</FormText>
                    </Info>
                    <Info style={{ width: 80 }}>
                      <FormRemark>{t("translation:admin.account.list_field.lanID")}</FormRemark>
                      <FormText>{item.LanID}</FormText>
                    </Info>
                    <Info style={{ width: 220 }}>
                      <FormRemark>{t("translation:admin.account.list_field.email")}</FormRemark>
                      <FormText>{item.staffEmail}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.account.list_field.createdt")}</FormRemark>
                      <FormText>{item.createdt.substr(0, 10) ?? "-"}</FormText>
                    </Info>
                  </ItemLeft>

                  <ItemRight>
                    {
                      allowEdit(role, item.role) && <>
                        <IconBtn onClick={() => {
                          loadAdmUpdateStaffStatus(item.staffId, item.status === "A" ? "D" : "A");
                        }}>
                          {
                            item.status === "A" ?
                              <Active>{t("translation:admin.account.active")}</Active>
                              :
                              <Inactive>{t("translation:admin.account.inactive")}</Inactive>
                          }
                        </IconBtn>
                        <IconBtn onClick={async () => {
                          resetAccountModal();
                          await loadAdmOpenStaff(item.staffId);

                          openModalConfirm({
                            title: t("translation:admin.account.edit_account.title"),
                            text: t("translation:admin.account.edit_account.subtitle"),
                            modalConfirmPrimaryButtonText: t("save"),
                            modalConfirmSecondaryButtonText: t("back"),
                            closeManually: true,
                            content: <ContentAccountForm />,
                            style: { content: { width: 720 } },
                            callback: async () => {
                              const { role, firstName, lastName, email, lanId } = useAdminAccountStore.getState();
                              const { setIsValid_role, setIsValid_firstName, setIsValid_lastName, setIsValid_email, setIsValid_lanId } = useAdminAccountStore.getState();

                              let isValid = true;

                              if (!role) {
                                isValid = false;
                                setIsValid_role(false);
                              }

                              if (firstName === "") {
                                isValid = false;
                                setIsValid_firstName(false);
                              }

                              if (lastName === "") {
                                isValid = false;
                                setIsValid_lastName(false);
                              }

                              if (email === "") {
                                isValid = false;
                                setIsValid_email(false);
                              } else if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(email)) {
                                isValid = false;
                                setIsValid_email(false);
                              }

                              if (lanId === "") {
                                isValid = false;
                                setIsValid_lanId(false);
                              }

                              if (isValid) {
                                await loadAdmUpdateStaff(item.staffId);
                                closeModalConfirm();

                                openModalInfo({
                                  title: "Edit Account",
                                  isShowSuccessIcon: true,
                                  text: `Account has been modified`,
                                });
                              }
                            },
                          });
                        }}>
                          <Icon src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt={t("edit")} />
                        </IconBtn>
                      </>
                    }
                  </ItemRight>
                </Item>
              );
            })
          }
        </ItemList>

        {
          (page && recordperpage && ttlpage) &&
          <PaginationContainer>
            <Pagination current={page} total={recordperpage * ttlpage} showSizeChanger={false} onChange={(page, pageSize) => {
              setPage(page)
            }} />
          </PaginationContainer>
        }
      </PageContainer>
    </PageApplication>
  );
}

const PageApplication = styled(Flex)`
  width: 100%;
  height: 100%;
  background-color: var(--aia-bx-gray-10);
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  gap: 0px;
`;

const PageContainer = styled(Flex)`
  padding: 24px 36px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`;

const MenuContainer = styled(Flex)`
  width: 100%;
  align-items: flex-end;
  gap: 0;
`;

const MenuItem = styled.div`
  display: flex;
  margin: 0px 24px;
  flex-direction: row;
  gap: 8px;
`;

const MenuItemText = styled.div`
  font-size: 14px;
  color: var(--aia-bx-dark-blue);
  text-shadow: 0 0 0 transparent;
  font-weight: normal;
  transition: font-weight 0.3s ease;
  ${(props) => props.$active && `font-weight: bold;`}
`;

const MenuItemCount = styled.div`
  color: var(--aia-bx-blue-2);
  text-shadow: 0 0 0 transparent;
  font-weight: normal;
  transition: font-weight 0.3s ease;
  ${(props) => props.$active && `font-weight: bold;`}
`;

const MenuExtend = styled.div`
  line-height: 46px;
  border: 0;
  border-bottom: 1px solid rgba(5, 5, 5, 0.06);
  box-shadow: none;
`;

const ExportBtn = styled.button`
  border: none;
  background-color: var(--aia-bx-blue-2);
  cursor: pointer;
  padding: 6px 24px;
  border-radius: 50px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:hover {
    background-color: var(--aia-bx-blue-6);
  }
`;

const ExportBtnIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const ExportBtnText = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: var(--aia-bx-white);
`;

const ToolBar = styled(ShadowBox)`
  border-radius: 50px;
  padding: 8px;
  justify-content: space-between;
  width: fit-content;
  min-width: 100%;
`;

const ToolBarLeft = styled(Flex)`
  gap:8px;
`;

const ToolBarRight = styled(Flex)`
  gap: 8px;
  padding-right: 24px;
`;

const FilterItem = styled.button`
  border: none;
  background: none;
  display: flex;
  padding: 16px;
  gap: 16px;
  cursor: pointer;
  border-radius: 5px;
  width: 100%;
  min-width: 180px;

  &:hover {
    background-color: var(--aia-bx-bright-blue-10);
  }
`;

const CheckboxImg = styled.img`
  width: 24px;
  height: 24px;
`;

const SearchIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const ToolBarItem = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  border-radius: 5px;

  &:hover {
    background-color: var(--aia-bx-bright-blue-10);
  }
`;

const ToolBarItemIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const ToolBarItemText = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: var(--aia-bx-dark-blue);
`;

const ItemList = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  height: calc(100vh - 416px);
  overflow: auto;
  padding: 5px;
  margin: -5px;
`;

const fadeIn = keyframes`
  from {
    transition: opacity 0.5s ease-in-out;
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Item = styled(ShadowBox)`
  padding: 0px;
  justify-content: space-between;
  width: max-content;
  min-width: 100%;
  animation: ${fadeIn} 0.5s ease-out;
`;

const ItemLeft = styled(Flex)`
  padding: 16px 32px;
  gap: 40px;
  flex-shrink: 0;
`;

const Info = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  min-width: 80px;
`;

const ItemRight = styled(Flex)`
  gap: 0px;
  height: 100%;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;
  border-left: 1px solid var(--aia-bx-grey-20);

  ${({ isLast }) => isLast && `
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  
  `}

  &:hover {
    background-color: var(--aia-bx-bright-blue-10);
  }
`;

const Icon = styled.img`
  width: 32px;
  height: 32px;
`;

const PaginationContainer = styled(ShadowBox)`
  width: auto;
  align-self: center;
`;

const Active = styled(FormText)`
  color: var(--aia-bx-green-70);
  font-weight: bold;
`;

const Inactive = styled(FormText)`
  color: var(--aia-bx-aiaRed-60);
  font-weight: bold;
`;

export default AdminAccount;